import { Component, inject, OnInit, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { AssistantType, FunctionInterface } from '@vendasta/ai-assistants';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { InfiniteScrollTriggerComponent } from '@vendasta/galaxy/infinite-scroll-trigger';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { combineLatest, firstValueFrom, map, Observable, switchMap } from 'rxjs';
import { AiAssistantI18nModule } from '../../../assets/i18n/ai-assistant-i18n.module';
import { AiAssistantService } from '../../../core/services/ai-assistant.service';
import { createConstraintFilters } from '../../../core/services/constraint-utils';
import { ACCOUNT_GROUP_ID_TOKEN, PARTNER_ID_TOKEN } from '../../../core/tokens';
import { AiToolsTableComponent } from '../ai-tools-table/ai-tools-table.component';

export type AiToolSelectDialogResult =
  | {
      selectedFunctions?: FunctionInterface[];
      newFunction?: boolean;
    }
  | null
  | undefined;

export type AiToolSelectDialogData = {
  excludedFunctionIds: string[];
  assistantType?: AssistantType;
};

@Component({
  selector: 'ai-tool-select-dialog',
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    GalaxyBadgeModule,
    AiAssistantI18nModule,
    GalaxyLoadingSpinnerModule,
    InfiniteScrollTriggerComponent,
    AiToolsTableComponent,
  ],
  templateUrl: './ai-tool-select-dialog.component.html',
  styleUrls: ['./ai-tool-select-dialog.component.scss', '../../ai-assistant-configuration/ai-assistant-shared.scss'],
})
export class AiToolSelectDialogComponent implements OnInit {
  private readonly accountGroupId$ = inject(ACCOUNT_GROUP_ID_TOKEN);
  private readonly partnerId$ = inject(PARTNER_ID_TOKEN);
  private readonly aiAssistantService = inject(AiAssistantService);
  private readonly dialogRef = inject(MatDialogRef<AiToolSelectDialogComponent>);
  protected readonly excludedFunctionIds: string[] = inject(MAT_DIALOG_DATA).excludedFunctionIds || [];
  protected readonly assistantType: AssistantType = inject(MAT_DIALOG_DATA).assistantType;
  private readonly snackbarService = inject(SnackbarService);
  protected readonly isLoading = signal(false);
  protected readonly functions = signal<FunctionInterface[]>([]);
  protected readonly nextCursor = signal('');
  protected readonly hasMore = signal(true);
  protected readonly tableSelectedFunctions = signal<FunctionInterface[]>([]);

  protected setTableSelectedFunctions(functions: FunctionInterface[]) {
    this.tableSelectedFunctions.set(functions);
  }

  ngOnInit() {
    this.loadFunctions();
  }

  private getFilteredFunctions(
    cursor: string,
  ): Observable<{ functions: FunctionInterface[]; nextCursor: string; hasMore: boolean }> {
    return combineLatest([this.accountGroupId$, this.partnerId$]).pipe(
      switchMap(([accountGroupId, partnerId]) => {
        const constraintFilters = createConstraintFilters(partnerId, accountGroupId, this.assistantType);

        return this.aiAssistantService.listFunctions({
          includeGlobal: true,
          cursor,
          constraintFilters,
        });
      }),
      map((response) => ({
        ...response,
        functions: (response?.functions || []).filter(
          (aiFunc) => aiFunc.id && !this.excludedFunctionIds.includes(aiFunc.id),
        ),
      })),
    );
  }

  async loadFunctions(): Promise<void> {
    if (!this.isLoading() && this.hasMore()) {
      this.isLoading.set(true);
      try {
        const nextFunctions = await firstValueFrom(this.getFilteredFunctions(this.nextCursor()));

        this.functions.update((oldFunctions) => [...oldFunctions, ...(nextFunctions?.functions || [])]);
        this.nextCursor.set(nextFunctions?.nextCursor ?? '');
        this.hasMore.set(nextFunctions?.hasMore ?? false);
      } catch (error) {
        this.snackbarService.openErrorSnack('AI_ASSISTANT.FUNCTIONS.ADD_FUNCTION.ERROR_LOADING_FUNCTIONS');
      } finally {
        this.isLoading.set(false);
      }
    }
  }

  chooseFunctions(functions: FunctionInterface[]) {
    this.dialogRef.close({
      selectedFunctions: functions,
    });
  }

  newFunction() {
    this.dialogRef.close({
      newFunction: true,
    });
  }
}
